import { router } from "@/router";
import { VUE_APP_NAME } from "@/commons/config";

import JwtDecode from "jwt-decode";
import BeneficiarioStore from "@/store/modules/beneficiario";
import GlobalStore from "@/store/modules/global";

import { map, tap } from "rxjs/operators";
import { BehaviorSubject, Observable } from "rxjs";
import { BeneficiarioAPI } from "@/api/beneficiario.api";
import { Beneficiario } from "@/models/beneficiario";
import { Acampanhamento } from "@/models/acompanhamento";
import BeneficiarioService from "./beneficiario.service";
import { BeneficiarioJwtDecode } from './beneficiario.auth.service';
import { ExpireJwtDecode } from './transportadora.auth.service';

class BeneficiarioAuthService {
  private readonly AUTH: string = `${VUE_APP_NAME}-beneficiario-ceara-app-auth`;
  private readonly CPF_NASCIMENTO_STORE: string = `${VUE_APP_NAME}-cpf-nascimento`;
  private readonly beneficiarioApi = new BeneficiarioAPI();

  private beneficiarioSubject = new BehaviorSubject(this.currentBeneficiario());
  private cpfNascimentoSubject = new BehaviorSubject(
    this.currentCpfNascimento()
  );

  router = router;

  constructor() {
    this.observableChangeCpfNascimento();
    this.observableChangeBeneficiario();
  }

  public currentBeneficiarioValue(): Beneficiario {
    return this.beneficiarioSubject.value;
  }

  public currentCpfNascimentoValue() {
    return this.cpfNascimentoSubject.value;
  }

  public currentCpfNascimento() {
    const cpfNascimento =
      JSON.parse(localStorage.getItem(this.CPF_NASCIMENTO_STORE)) || null;

    if (!cpfNascimento) return null;

    return cpfNascimento;
  }

  public login(
    cpf: string,
    dataNascimento: string,
    recaptcha: string
  ): Observable<Beneficiario> {
    return this.beneficiarioApi
      .login(cpf, dataNascimento, recaptcha)
      .pipe(map(response => this.setSession(response, cpf, dataNascimento)));
  }

  public logout(type?: string, message?: string) {
    localStorage.removeItem(this.CPF_NASCIMENTO_STORE);
    localStorage.removeItem(this.AUTH);

    this.beneficiarioSubject.next(null);

    GlobalStore.setSnackBar({
      show: true,
      message: message || "Saiu com sucesso!.",
      type: type || "success"
    });

    router.push({ name: "ceara-app-passe-livre" });
  }

  public setSession(authResult: any, cpf: string, nascimento: string): any {
    if (authResult && authResult.token) {
      this.buildAuth(authResult);

      this.beneficiarioSubject.next(this.currentBeneficiario());

      router.push({
        name: "ceara-app-passe-livre-detalhe",
        params: { id: String(this.beneficiarioSubject.value.id) }
      });

      GlobalStore.setSnackBar({
        show: true,
        message: "Login feito com sucesso.",
        type: "success"
      });

      return this.beneficiarioSubject.value;
    } else {
      this.buildCpfNascimento({ cpf: cpf, nascimento: nascimento });

      this.cpfNascimentoSubject.next(this.currentCpfNascimento());

      router.push({ name: "ceara-app-passe-livre-cadastro" });

      return this.cpfNascimentoSubject.value;
    }
  }

  public async setSessionFromRegister(authResult: any) {
    if (authResult) {
      this.buildAuth(authResult);

      localStorage.removeItem(this.CPF_NASCIMENTO_STORE);

      this.beneficiarioSubject.next(this.currentBeneficiario());

      const processos = await BeneficiarioService.beneficiarioProcessos(
        this.beneficiarioSubject.value.id
      ).toPromise();

      router.push({
        name: "ceara-app-passe-livre-confirmar-cadastro",
        params: {
          id: String(this.beneficiarioSubject.value.id),
          processo: processos[0].id
        }
      });

      return this.beneficiarioSubject.value;
    }
  }

  public hasFinishedRegister(processo: any): boolean {
    const hasFinish = processo.acompanhamentos.map(
      (acomp: Acampanhamento) => Number(acomp.situacao.id) === 2
    );

    // retorna true Cadastro Estiver Finalizado
    return hasFinish.includes(true);
  }

  public hasInclusionProcess(processo: any): boolean {
    // retorna true se o tipo de processo for Inclusão
    return Number(processo.tipoAtendimento.id) === 1;
  }

  public token(): string {
    const { token = null } = JSON.parse(localStorage.getItem(this.AUTH)) || {};
    return token;
  }

  public expireToken(): number {
    const { token = null } = JSON.parse(localStorage.getItem(this.AUTH)) || {};

    if (!token) return null;

    const { exp } = JwtDecode<ExpireJwtDecode>(token);
    return exp * 1000;
  }

  public async changeSession(beneficiario: Beneficiario) {
    await this.buildAuth(beneficiario);

    this.beneficiarioSubject.next(this.currentBeneficiario());

    return this.beneficiarioSubject.value;
  }

  private observableChangeCpfNascimento() {
    this.cpfNascimentoSubject.subscribe((data: any) => {
      if (BeneficiarioStore) {
        BeneficiarioStore.setBeneficiarioCpfNascimento(data);
      }
    });
  }

  private observableChangeBeneficiario() {
    this.beneficiarioSubject.subscribe((beneficiario: Beneficiario) => {
      if (BeneficiarioStore) {
        BeneficiarioStore.setBeneficiario(beneficiario);
      }
    });
  }

  private buildAuth = (beneficiario: Beneficiario) => {
    localStorage.setItem(this.AUTH, JSON.stringify(beneficiario));
  };

  private buildCpfNascimento = (data: { cpf: string; nascimento: string }) => {
    localStorage.setItem(this.CPF_NASCIMENTO_STORE, JSON.stringify(data));
  };

  private currentBeneficiario(): Beneficiario {
    const { token = null } = JSON.parse(localStorage.getItem(this.AUTH)) || {};

    if (!token) return null;

    const {
      sub,
      nome,
      numeroDocumento,
      dataEmissaoDocumento,
      orgaoEmissorDocumento,
      ufDocumento,
      sexo,
      dataNascimento,
      cpf,
      dddTelefone,
      telefone,
      logradouro,
      numeroLogradouro,
      bairro,
      municipio,
      cep,
      nomeMae,
      acompanhante,
      processos,
      email
    } = JwtDecode<BeneficiarioJwtDecode>(token);

    return new Beneficiario(
      sub,
      nome,
      numeroDocumento,
      dataEmissaoDocumento,
      orgaoEmissorDocumento,
      ufDocumento,
      sexo,
      dataNascimento,
      cpf,
      dddTelefone,
      telefone,
      logradouro,
      numeroLogradouro,
      bairro,
      municipio,
      cep,
      nomeMae,
      //@ts-ignore
      acompanhante,
      processos,
      "",
      email
    );
  }
}

export default new BeneficiarioAuthService();
